import {
  ColumnsConfig,
  getColumnDisplayName,
  TABLE_COLUMN_WIDTH,
} from "src/cases/caseView/blocks/cellTypes";
import {
  DiffTableHeaderCell,
  DiffTableValueCell,
} from "src/cases/caseView/history/events/diffTableCells";
import { isCellChanged } from "src/cases/caseView/history/events/tableBlockDiff";
import { TableBlockSingleRecord } from "src/cases/queries/casesTypes";

type SingleRecordDiffTableProps = {
  beforeRecord: TableBlockSingleRecord;
  afterRecord: TableBlockSingleRecord;
  columnsConfig: ColumnsConfig;
};

type DiffSide = "before" | "after";

const PROPERTY_COLUMN_STYLE = { width: TABLE_COLUMN_WIDTH } as const;

// A property only on the after side was added (green), one only on the
// before side was removed (red), and a changed value highlights the after
// side (yellow) — the same tints the row-based diff uses.
const getCellTint = (
  before: TableBlockSingleRecord,
  after: TableBlockSingleRecord,
  key: string,
  side: DiffSide,
): string | false => {
  const inBefore = key in before;
  const inAfter = key in after;
  if (!inBefore) return side === "after" && "bg-green-50";
  if (!inAfter) return side === "before" && "bg-red-50";
  return (
    side === "after" && isCellChanged({ before, after }, key) && "bg-yellow-50"
  );
};

/** One row per property, with the before and after values side by side —
 *  the history counterpart of the block's single-record key/value list. */
export const SingleRecordDiffTable: React.FC<SingleRecordDiffTableProps> = ({
  beforeRecord,
  afterRecord,
  columnsConfig,
}) => {
  const keys = Array.from(
    new Set([...Object.keys(beforeRecord), ...Object.keys(afterRecord)]),
  );

  return (
    <div
      className="decideScrollbar overflow-x-auto"
      data-loc="table-changes-modal-single-table"
    >
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <DiffTableHeaderCell style={PROPERTY_COLUMN_STYLE}>
              Property
            </DiffTableHeaderCell>
            <DiffTableHeaderCell muted>Before</DiffTableHeaderCell>
            <DiffTableHeaderCell>After</DiffTableHeaderCell>
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => (
            <tr key={key}>
              <PropertyCell columnsConfig={columnsConfig} columnKey={key} />
              <DiffTableValueCell
                columnKey={key}
                columnsConfig={columnsConfig}
                dataLoc={`table-changes-single-before-${key}`}
                hasValue={key in beforeRecord}
                tint={getCellTint(beforeRecord, afterRecord, key, "before")}
                value={beforeRecord[key]}
                muted
              />
              <DiffTableValueCell
                columnKey={key}
                columnsConfig={columnsConfig}
                dataLoc={`table-changes-single-after-${key}`}
                hasValue={key in afterRecord}
                tint={getCellTint(beforeRecord, afterRecord, key, "after")}
                value={afterRecord[key]}
              />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Row header naming the property, shared with the single-record snapshot. */
export const PropertyCell: React.FC<{
  columnKey: string;
  columnsConfig: ColumnsConfig;
}> = ({ columnKey, columnsConfig }) => (
  <th
    className="border border-gray-100 p-0 text-left align-top"
    data-loc={`table-changes-property-${columnKey}`}
    scope="row"
    style={PROPERTY_COLUMN_STYLE}
  >
    <div className="flex min-w-0 items-start px-1.5 py-1.5 font-inter-medium-12px text-gray-800">
      <span className="min-w-0 break-words">
        {getColumnDisplayName(columnKey, columnsConfig)}
      </span>
    </div>
  </th>
);
